import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import type { OrganizationSummary } from '../lib/types';
import { useWorkspace } from '../store/workspace';
import { initials } from '../lib/when';
import { switchDestination } from './nav';
import { Icon } from './Icon';

/**
 * Past this many, the menu carries its own search box.
 *
 * Low, because the box is also how anybody learns the search is there. Below
 * it the menu is a short list with the current centre already focused, and a
 * field above four rows would be in the way rather than useful.
 */
const SEARCH_FROM = 4;

/**
 * Moving between centres without leaving the screen you are on.
 *
 * A platform administrator with ten centres was switching by going out to the
 * list, finding the row and pressing Open — two clicks and a page change to
 * answer a question they already knew the answer to. The rail already says
 * which centre is open; this makes that label the way to change it.
 *
 * The list is fetched when the menu is first opened rather than on every page:
 * most sessions never switch, and the rail should not cost a request to draw.
 */
export function CentreSwitcher({ onLeave, name }: { onLeave: () => void; name: string }) {
  const organizationId = useWorkspace((w) => w.organizationId);
  const choose = useWorkspace((w) => w.choose);
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [centres, setCentres] = useState<OrganizationSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const wrap = useRef<HTMLDivElement | null>(null);
  const button = useRef<HTMLButtonElement | null>(null);
  const search = useRef<HTMLInputElement | null>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);

  const shown = useMemo(() => {
    const all = centres ?? [];
    const needle = query.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (c) => c.name.toLowerCase().includes(needle) || c.slug.toLowerCase().includes(needle),
    );
  }, [centres, query]);

  // A route change means the switch already happened, or the administrator
  // went somewhere else entirely. Either way the menu has had its answer.
  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;

    const onDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Opening reads the list once and keeps it; a centre added since is picked
  // up the next time the console loads, which is when it can matter.
  useEffect(() => {
    if (!open || centres || loading) return;
    setLoading(true);
    api
      .get<{ organizations: OrganizationSummary[] }>('/organizations')
      .then(({ data }) => {
        setCentres(data.organizations);
        setError(null);
      })
      .catch((err) => setError(errorMessage(err, 'Could not load your centres')))
      .finally(() => setLoading(false));
  }, [open, centres, loading]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // The search box where there is enough to search, the current centre
    // otherwise: either way the keyboard is already where it is needed.
    if ((centres?.length ?? 0) >= SEARCH_FROM) search.current?.focus();
    else items.current[0]?.focus();
  }, [open, centres]);

  function move(delta: number) {
    if (shown.length === 0) return;
    // Arriving from the search box, the first press should land on the first
    // result rather than skipping it — the box is above the list, not in it.
    const fromSearch = document.activeElement === search.current;
    const next = fromSearch
      ? delta > 0
        ? 0
        : shown.length - 1
      : (active + delta + shown.length) % shown.length;
    setActive(next);
    items.current[next]?.focus();
  }

  function pick(centre: OrganizationSummary) {
    setOpen(false);
    if (centre.id === organizationId) return;
    choose({ id: centre.id, name: centre.name });
    navigate(switchDestination(location.pathname));
  }

  const current = centres?.find((c) => c.id === organizationId);

  return (
    <div className="switcher" ref={wrap}>
      {/* The whole card is the way to another centre: what is open, and the
          place to change it, are the same thing to point at. */}
      <button
        type="button"
        ref={button}
        className="scope__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Working inside ${name}. Switch centre`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="monogram monogram--sm" aria-hidden="true">
          {initials(name)}
        </span>
        <span className="scope__text">
          <span className="scope__label">Working inside</span>
          <span className="scope__name" title={name}>
            {name}
          </span>
        </span>
        <Icon name="chevronUpDown" size={16} className="scope__chev" />
      </button>

      {open && (
        <div
          className="switcher__menu"
          role="menu"
          aria-label="Centres"
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(1);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(-1);
            } else if (event.key === 'Home') {
              event.preventDefault();
              setActive(0);
              items.current[0]?.focus();
            } else if (event.key === 'End') {
              event.preventDefault();
              const last = shown.length - 1;
              setActive(last);
              items.current[last]?.focus();
            }
          }}
        >
          {(centres?.length ?? 0) >= SEARCH_FROM && (
            <input
              ref={search}
              className="switcher__search"
              placeholder="Find a centre"
              value={query}
              aria-label="Find a centre"
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && shown[0]) pick(shown[0]);
              }}
            />
          )}

          {loading && <p className="switcher__note">Loading…</p>}
          {error && <p className="switcher__note switcher__note--error">{error}</p>}
          {!loading && !error && shown.length === 0 && (
            <p className="switcher__note">
              {query ? `Nothing matching “${query.trim()}”.` : 'No other centres yet.'}
            </p>
          )}

          <div className="switcher__list">
            {shown.map((centre, index) => (
              <button
                key={centre.id}
                type="button"
                role="menuitem"
                ref={(el) => {
                  items.current[index] = el;
                }}
                className={`switcher__item ${centre.id === organizationId ? 'switcher__item--on' : ''}`}
                onFocus={() => setActive(index)}
                onClick={() => pick(centre)}
              >
                <span className="monogram monogram--sm" aria-hidden="true">
                  {initials(centre.name)}
                </span>
                <span className="switcher__text">
                  <span className="switcher__name">{centre.name}</span>
                  <span className="switcher__meta">
                    {centre.isActive ? (
                      <>
                        <span className="mono">{centre.members.students}</span> student
                        {centre.members.students === 1 ? '' : 's'}
                      </>
                    ) : (
                      'Suspended'
                    )}
                  </span>
                </span>
                {centre.id === organizationId && (
                  <span className="switcher__tick" aria-label="Open now">
                    <Icon name="check" size={16} />
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="switcher__foot">
            <button
              type="button"
              className="scope__action"
              onClick={() => {
                setOpen(false);
                navigate('/admin/organizations');
              }}
            >
              All centres
            </button>
            <button
              type="button"
              className="scope__action"
              onClick={() => {
                setOpen(false);
                onLeave();
              }}
            >
              Leave {current ? 'this centre' : ''}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
